import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-stone-800 font-serif">
      {/* Navigation */}
      <nav className="bg-white shadow-md py-3">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="font-sans font-semibold text-stone-800 tracking-wide text-sm">
            COMMUNICATE WITH INTELLIGENCE™
          </Link>
          <Link to="/" className="text-sm font-sans font-semibold text-amber-700 hover:text-amber-800 transition-colors">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Privacy Policy</h1>
        <div className="h-1 w-16 bg-amber-600 mb-10" />

        <div className="space-y-8 text-stone-600 leading-relaxed">
          <p className="text-stone-800 font-semibold text-lg">
            IMPORTANT: PLEASE READ BEFORE USING OUR WEBSITES AND PLATFORMS.
          </p>

          <p>
            This privacy policy (the "Privacy Policy") describes how your personal information is collected, used and shared when you visit, use, or make a purchase from our websites (the "Sites"). Throughout this Privacy Policy, the terms "we", "us", and "our" refer to Communicate With Intelligence and Soul-Full-Living. (CWI, SFL), and the terms "you" and "your" refer to the user of our Sites. This policy applies to all users of the Sites. By using the Sites you expressly agree to comply with and be bound by this policy. This includes any and all additional terms, conditions and policies referenced and/or made available herein. If you do not agree to these Authorized User Terms, you may not access or use the Sites.
          </p>

          <p>
            This privacy policy has been compiled to better serve those who are concerned with how their 'Personally Identifiable Information' (PII) is being used online. PII, as described in US privacy law and information security, is information that can be used on its own or with other information to identify, contact, or locate a single person or to identify an individual in context.
          </p>

          <p>
            Please read our privacy policy carefully to get a clear understanding of how we collect, use, protect, or otherwise handle your PII in accordance with our websites.
          </p>

          {/* Section: Personal Information We Collect */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              Personal Information We Collect From Visiting Our Websites or Apps
            </h2>
            <div className="space-y-4">
              <p>
                When you visit the Sites we may automatically collect certain information from your device, including without limitation information about your web browser and I.P. address. As you browse the Sites or participate in a course or membership, we may collect information about the individual web pages or products that you view, what websites or search terms referred you to the Sites, and information about how you interact with the Sites (such collected information is hereinafter referred to as the "Device Information").
              </p>
              <p>
                We may use "cookies" on our Sites. A cookie is a piece of data stored on a Site visitor's hard drive, which may be used by us to help us improve our Servers, our Sites and your access to them, and which identifies repeat visitors to our Sites. Cookies also enable us to track and target the interests of our users to enhance their browsing experience. Our utilization of cookies is not linked to any personally identifiable information on our Sites.
              </p>
              <p>
                If you make a purchase or attempt to make a purchase through our Sites, we may collect certain information from you, including without limitation your name, billing address, shipping address, payment information (including credit card information), email address, and phone number (such information is hereinafter referred to as "Order Information").
              </p>
              <p>
                In this Privacy Policy, "Personal Information" shall mean both Device Information and Order Information.
              </p>
            </div>
          </div>

          {/* Section: When We Collect */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              When We Collect Information
            </h2>
            <p>
              We collect information from you when you respond to an ad, join a membership, purchase a course, enter information on our sites, or provide us with feedback on our products or services.
            </p>
          </div>

          {/* Section: How We Use */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              How We Use Your Information
            </h2>
            <div className="space-y-4">
              <p>
                We may use the information we collect from you when you register, make a purchase, sign up for additional memberships/courses, respond to a survey or marketing communication, surf the website, or use certain other site features in the following ways:
              </p>
              <p>
                We use the Order Information that we collect to, among other things, communicate with you and fulfill and process any orders placed through the Sites (including without limitation processing your payment information and providing invoices and/or order confirmations).
              </p>
              <p>We use the Device Information that we collect to help us screen for potential risks and, more generally, to improve and optimize our Sites and Servers. Examples include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personalizing your experience and allow us to deliver the type of content and product offerings you are most interested in.</li>
                <li>Improving our website to better serve you.</li>
                <li>Allowing us to better serve you in responding to your customer service requests.</li>
                <li>Administering a contest, promotion, survey, or other site feature.</li>
                <li>Asking for ratings and reviews of services or products.</li>
                <li>Following up with you after correspondence.</li>
              </ul>
            </div>
          </div>

          {/* Section: Sharing */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              Sharing Your Personal Information
            </h2>
            <div className="space-y-4">
              <p>
                We take precautions to protect your Personal Information. Whenever we collect sensitive Personal Information, such information is encrypted and transmitted, if necessary, by secure servers.
              </p>
              <p>
                You acknowledge and agree that we use a third party for processing credit card transactions and, by using the Sites or our Servers, you hereby consent to the transmission of your credit card information to such third party.
              </p>
              <p>
                We will not share your Personal Information without your prior written consent, unless we are required to do so to comply with applicable laws and regulations, or to otherwise protect our rights.
              </p>
              <p>
                No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. Information sharing to subcontractors in support services, such as customer service is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.
              </p>
            </div>
          </div>

          {/* Section: Third Parties */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              Third Parties
            </h2>
            <p>
              You acknowledge that we have no access to or control over third-party cookies and third-party websites. This Privacy Policy does not extend to the websites of any third parties. You acknowledge and agree that we are not responsible for the content or privacy practices of any third parties.
            </p>
          </div>

          {/* Section: Data Retention */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              Data Retention
            </h2>
            <p>
              When you make a purchase through our Sites, we may maintain Order Information for our records, unless and until you ask us to delete such information. We promise to keep your personal information safe and not to disclose your personal information.
            </p>
          </div>

          {/* Section: Changes */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              This Privacy Policy May Change
            </h2>
            <p>
              This Privacy Policy is current as of the date in which you access the Privacy Policy. We reserve the right to change this Privacy Policy from time to time and without notice to you. Any such changes will be effective as of the date we post the revised version of this Privacy Policy. If at any time you do not accept this Privacy Policy, you may not use the Sites. You can review the most current version of this Privacy Policy at any time by visiting our webpages.
            </p>
          </div>

          {/* Section: Your Rights */}
          <div>
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              Your Rights
            </h2>
            <div className="space-y-4">
              <p>Under applicable law, you have the following rights:</p>
              <p>
                <strong className="text-stone-800">Access to your information:</strong> You have the right to request a copy of the personal information we hold about you.
              </p>
              <p>
                <strong className="text-stone-800">Correcting your information:</strong> We want to have accurate personal information about you. Please contact us if you think the personal information we hold is not up to date or correct and we will correct it for you.
              </p>
              <p>
                <strong className="text-stone-800">Deletion of your information:</strong> You have the right to ask us to delete personal information about you if it no longer is required for the purpose it was collected, you have withdrawn your consent, you have a valid objection to us using your personal information, or our use of your personal information is contrary to law or our other legal obligations.
              </p>
              <p>
                <strong className="text-stone-800">Objecting to how we may use your information:</strong> You have the right at any time to require us to stop using your personal information for direct marketing purposes. In addition, where we use your personal information on legitimate interest grounds then, if you ask us to, we will stop using that personal information unless there are overriding legitimate grounds to continue.
              </p>
              <p>
                <strong className="text-stone-800">Restricting how we may use your information:</strong> In some cases, you may ask us to restrict how we use your personal information. This right might apply, for example, where we are checking the accuracy of personal information about you that we hold or assessing the validity of any objection you have made to our use of your information. The right might also apply where there is no longer a basis for using your personal information but you don't want us to delete the information. Where this right is validly exercised, we may only use the relevant personal information with your consent, for legal claims or where there are other public interest grounds to do so.
              </p>
              <p>
                <strong className="text-stone-800">Automated processing:</strong> If we use your personal information on an automated basis to make decisions that significantly affect you, you have the right to ask that the decision be reviewed by an individual to whom you may make representations and contest the decision. This right only applies where we use your information with your consent or as part of a contractual relationship with you.
              </p>
              <p>
                <strong className="text-stone-800">Withdrawing consent using your personal information:</strong> Where we use your personal information with your consent you may withdraw that consent at any time and we will stop using your personal information for the purpose(s) for which consent was given. Note, however, that this will not affect any processing that has already taken place.
              </p>
              <p>
                Please contact us if you wish to exercise any of these rights. You can find the contact details below.
              </p>
            </div>
          </div>

          {/* Section: Contact */}
          <div className="bg-stone-50 rounded-lg p-8 mt-4">
            <h2 className="text-xl font-bold text-stone-900 mb-4 font-sans tracking-wide uppercase text-sm">
              Contact Us
            </h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-3">
              <a href="mailto:mary@soul-full-living.com" className="text-amber-700 hover:text-amber-800 font-semibold transition-colors">
                Mary@soul-full-living.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 bg-stone-900 text-stone-400">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-sm">
          <div className="text-center md:text-left">
            <p className="text-stone-300 font-sans font-semibold tracking-wide mb-1">
              COMMUNICATE WITH INTELLIGENCE™
            </p>
            <p>Soul-Full-Living LLC</p>
          </div>
          <div className="text-center md:text-right space-y-1">
            <p>
              <a href="mailto:mary@soul-full-living.com" className="hover:text-white transition-colors">
                mary@soul-full-living.com
              </a>
            </p>
            <p>855-499-0279</p>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-8 pt-6 border-t border-stone-800 text-center text-xs text-stone-500">
          © {new Date().getFullYear()} Communicate with Intelligence — All Rights Reserved. &nbsp;
          <Link to="/privacy" className="hover:text-stone-300 transition-colors">Privacy Policy</Link>{' '}
          |{' '}
          <Link to="/terms" className="hover:text-stone-300 transition-colors">Terms and Conditions</Link>
        </div>
      </footer>
    </div>
  );
}
